/**
 * Prefetch Logout Guard
 * ---------------------------------------------------------------------------
 * PROBLEM
 * The Warehouse theme bundles instant.page (in theme.min.js) and the <body> uses
 * `data-instant-intensity="viewport"`, which prefetches EVERY same-origin link
 * that scrolls into the viewport. A prefetch is a real GET request WITH cookies,
 * so prefetching a logout URL (e.g. /customer_identity/logout for "New customer
 * accounts", or the classic /account/logout) silently destroys the customer
 * session — logging the user out just by scrolling / navigating.
 *
 * Theme-internal logout links carry `data-no-instant`, but links coming from
 * admin-managed menus (rendered via {{ link.url }} in header/footer) do NOT, so
 * they can slip through.
 *
 * SOLUTION
 * 1. Network-level block (bulletproof, timing-independent): instant.page issues
 *    prefetches via `document.head.appendChild(<link rel="prefetch">)`. We wrap
 *    that insertion and drop it for unsafe URLs BEFORE any request is sent.
 * 2. Defense in depth: tag matching <a> links with `data-no-instant` so
 *    instant.page's own isPreloadable() check skips them on hover/mousedown/
 *    viewport paths too. A MutationObserver keeps tagging dynamically added
 *    links (popovers, mini-cart, apps).
 *
 * LOADING
 * This file MUST be loaded synchronously (no defer/async) in the <head> BEFORE
 * theme.min.js, so the appendChild wrapper is installed before instant.page runs.
 */
(function () {
  "use strict";

  // Same-origin path fragments that must NEVER be prefetched (they end the session).
  var UNSAFE_PATTERNS = ["/account/logout", "/customer_identity"];

  function isUnsafeHref(href) {
    if (!href) return false;
    try {
      var url = new URL(href, window.location.origin);
      // Session cookies are host-scoped, only same-origin links matter here.
      if (url.origin !== window.location.origin) return false;
      var path = url.pathname.toLowerCase();
      for (var i = 0; i < UNSAFE_PATTERNS.length; i++) {
        if (path.indexOf(UNSAFE_PATTERNS[i]) !== -1) return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  function isUnsafePrefetchNode(node) {
    return (
      node &&
      node.tagName === "LINK" &&
      (node.rel === "prefetch" || node.rel === "prerender" || node.rel === "preload") &&
      isUnsafeHref(node.href)
    );
  }

  // 1) NETWORK-LEVEL BLOCK ----------------------------------------------------
  // Wrap head.appendChild / head.insertBefore so an unsafe prefetch <link> is
  // never inserted -> the browser never fires the request -> no logout.
  try {
    var head = document.head || document.getElementsByTagName("head")[0];
    if (head) {
      var nativeAppendChild = head.appendChild.bind(head);
      head.appendChild = function (node) {
        if (isUnsafePrefetchNode(node)) return node; // swallow silently
        return nativeAppendChild(node);
      };

      var nativeInsertBefore = head.insertBefore.bind(head);
      head.insertBefore = function (node, ref) {
        if (isUnsafePrefetchNode(node)) return node; // swallow silently
        return nativeInsertBefore(node, ref);
      };
    }
  } catch (e) {
    /* no-op: network block is best-effort, the DOM tagging below still applies */
  }

  // 2) DEFENSE IN DEPTH -------------------------------------------------------
  function tagLink(a) {
    if (a && a.tagName === "A" && !a.hasAttribute("data-no-instant") && isUnsafeHref(a.href)) {
      a.setAttribute("data-no-instant", "");
    }
  }

  function tagLinksIn(root) {
    if (!root || !root.querySelectorAll) return;
    var links = root.querySelectorAll("a[href]:not([data-no-instant])");
    for (var i = 0; i < links.length; i++) tagLink(links[i]);
  }

  function init() {
    tagLinksIn(document);

    try {
      var mo = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) continue;
            if (node.tagName === "A") tagLink(node);
            else tagLinksIn(node);
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {
      /* no-op */
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
